// src/pages/TeamPage.tsx

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import { useGetTeamMembersQuery } from '../api/teamApi';
import {
  useGetTeamRebalancingRecommendationsQuery,
  useGetManagementSummaryQuery,
} from '../api/managementApi';
import { useGetTasksQuery, useUpdateTaskMutation } from '../api/tasksApi';
import { useCreateConversationMutation, useAddMessageMutation } from '../api/conversationsApi';
import { trackAssistantLaunched } from '../services/aiAnalytics';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorState from '../components/common/ErrorState';
import Button from '../components/common/Button';
import TeamMemberDetailSlideOver from '../components/team/TeamMemberDetailSlideOver';
// TEAM-UX-1 — invite-role helpers (INVITABLE_ROLES / DEFAULT_INVITE_ROLE /
// getCentralRoleForCrmRole) are gone with the invite form. Membership
// authoring lives in Settings → Members, which resolves roles through
// Central. Only badge styling is still needed here.
import { getBadgeClasses } from '@levelup/crm-roles';
import type { TeamMemberSummary } from '../types/api';
import { useTaskPermissions } from '../hooks/useTaskPermissions';

function formatElapsedTime(clockInAt: string): string {
  const start = new Date(clockInAt).getTime();
  const now = Date.now();
  const diff = Math.floor((now - start) / 1000);

  const hours = Math.floor(diff / 3600);
  const minutes = Math.floor((diff % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

// ============================================================
// Team Scorecard — module-level types, config, helpers
// ============================================================

// Week bounds (Mon–Sun)
function getScorecardWeek() {
  const d = new Date();
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  const start = d.toISOString().split('T')[0];
  const e = new Date(d);
  e.setDate(e.getDate() + 6);
  return { start, end: e.toISOString().split('T')[0] };
}
const SCORECARD_WEEK = getScorecardWeek();
const TEAM_TODAY = new Date().toISOString().split('T')[0];

interface ScorecardEntry {
  userId: string;
  displayName: string;
  completedThisWeek: number;
  openTaskCount: number;
  overdueCount: number;
  blockedCount: number;
  unestimatedOpenCount: number;
  estimatedMinutes: number;
  avgCompletionScore: number | null;
  reopenedCount: number;
  upcomingThisWeek: number;
}

const SIGNAL_CONFIG: Record<string, { cls: string }> = {
  'Overloaded':         { cls: 'bg-red-100 text-red-700' },
  'At risk':            { cls: 'bg-amber-100 text-amber-700' },
  'Needs estimates':    { cls: 'bg-gray-100 text-gray-600' },
  'On top of work':     { cls: 'bg-green-100 text-green-700' },
  'Upcoming pressure':  { cls: 'bg-orange-100 text-orange-700' },
};

function deriveSignals(s: ScorecardEntry): string[] {
  const sigs: string[] = [];
  // 480 min = 8h
  if (s.openTaskCount > 10 || s.estimatedMinutes > 480) sigs.push('Overloaded');
  if (s.overdueCount > 0 || s.blockedCount > 0) sigs.push('At risk');
  // Predictive: high load + multiple tasks due this week → overload risk
  if (!sigs.includes('Overloaded') && s.openTaskCount > 7 && s.upcomingThisWeek >= 3)
    sigs.push('Upcoming pressure');
  if (s.unestimatedOpenCount > 3) sigs.push('Needs estimates');
  if (sigs.length === 0 && s.completedThisWeek > 0) sigs.push('On top of work');
  return sigs;
}

function buildMemberAIPrimer(s: ScorecardEntry): string {
  const lines: string[] = [];
  lines.push(`**Capacity Review — ${s.displayName}**`);
  lines.push('');
  lines.push('This week:');
  lines.push(`- Completed: ${s.completedThisWeek} tasks`);
  lines.push(`- Open: ${s.openTaskCount} tasks`);
  if (s.overdueCount > 0)        lines.push(`- Overdue: ${s.overdueCount}`);
  if (s.blockedCount > 0)        lines.push(`- Blocked: ${s.blockedCount}`);
  if (s.unestimatedOpenCount > 0) lines.push(`- Unestimated: ${s.unestimatedOpenCount} open tasks`);
  if (s.estimatedMinutes > 0)    lines.push(`- Estimated load: ~${Math.round(s.estimatedMinutes / 60)}h`);
  if (s.reopenedCount > 0)       lines.push(`- Tasks reopened this week: ${s.reopenedCount}`);
  if (s.avgCompletionScore != null) lines.push(`- Avg quality score: ${Math.round(s.avgCompletionScore)}/100`);
  const signals = deriveSignals(s);
  if (signals.length > 0) {
    lines.push('');
    lines.push(`Signals: ${signals.join(', ')}`);
  }
  lines.push('');
  lines.push(`Please help me understand how best to support ${s.displayName}. What should I focus on: resolving their blockers, redistributing their load, or helping them catch up on overdue work? Keep the response actionable and manager-friendly.`);
  return lines.join('\n');
}

export default function TeamPage() {
  const activeWorkspace = useActiveWorkspace();
  const workspaceId = activeWorkspace?.workspace.id;

  const [selectedMember, setSelectedMember] = useState<TeamMemberSummary | null>(null);
  const [now, setNow] = useState(Date.now());

  // Update elapsed time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const {
    data: teamMembers,
    isLoading,
    error,
    refetch,
  } = useGetTeamMembersQuery({ workspaceId }, { skip: !workspaceId });


  const { data: rebalancing } = useGetTeamRebalancingRecommendationsQuery(undefined, {
    skip: !workspaceId,
  });
  const { data: tasksResponse } = useGetTasksQuery({ workspaceId }, { skip: !workspaceId });
  const { data: mgmt } = useGetManagementSummaryQuery(undefined, { skip: !workspaceId });
  const [updateTask] = useUpdateTaskMutation();
  const [createConversation] = useCreateConversationMutation();
  const [addMessage] = useAddMessageMutation();
  const [appliedTaskIds, setAppliedTaskIds] = useState<Set<string>>(new Set());

  // FU-4 Step 8 — the Reassign control calls `updateTask`, so its authority
  // is TASKS write, not anything in the `team` domain. `routes/team.ts`
  // carries only `requireCapabilityMiddleware('team', 'read')`; the mutation
  // this page performs belongs to `routes/tasks.ts`. Gating it on a team
  // capability would have been plausible and wrong.
  const taskPerms = useTaskPermissions();

  const handleApplyReassignment = async (taskId: string, toUserId: string) => {
    try {
      await updateTask({ id: taskId, assigneeUserId: toUserId }).unwrap();
      setAppliedTaskIds((prev) => new Set(prev).add(taskId));
    } catch (err) {
      console.error('Failed to apply reassignment:', err);
    }
  };

  // ── Scorecard derived ──────────────────────────────────────
  const scorecard = useMemo((): ScorecardEntry[] => {
    if (!teamMembers || teamMembers.length === 0) return [];
    const tasks = tasksResponse?.tasks ?? [];

    const overdueSet = new Set(mgmt?.gaps.overdueTaskIds ?? []);
    const blockedSet = new Set(mgmt?.gaps.blockedTaskIds ?? []);
    const capacityMap = new Map(
      (mgmt?.capacity ?? []).map((c) => [
        c.userId,
        { openTaskCount: c.openTaskCount, estimatedMinutes: c.estimatedMinutes, unestimatedTaskCount: c.unestimatedTaskCount },
      ])
    );

    // Per-user derived aggregates from tasks
    const deriveMap = new Map<string, {
      completedThisWeek: number; scoreSum: number; scoreCount: number; reopenedCount: number;
    }>();
    // Per-user overdue / blocked counts from gap sets
    const overdueByUser = new Map<string, number>();
    const blockedByUser = new Map<string, number>();
    // Per-user tasks due this week (not done) — for overload risk signal
    const upcomingByUser = new Map<string, number>();

    for (const t of tasks) {
      const uid = t.assigneeUserId;
      if (!uid) continue;
      if (!deriveMap.has(uid)) deriveMap.set(uid, { completedThisWeek: 0, scoreSum: 0, scoreCount: 0, reopenedCount: 0 });
      const d = deriveMap.get(uid)!;

      if (t.status === 'done') {
        const stamp = t.completedAt ?? t.updatedAt;
        if (stamp.split('T')[0] >= SCORECARD_WEEK.start && stamp.split('T')[0] <= SCORECARD_WEEK.end) {
          d.completedThisWeek++;
        }
        if (t.completionScore != null) { d.scoreSum += t.completionScore; d.scoreCount++; }
      } else {
        if (t.dueDate && t.dueDate.split('T')[0] >= TEAM_TODAY && t.dueDate.split('T')[0] <= SCORECARD_WEEK.end)
          upcomingByUser.set(uid, (upcomingByUser.get(uid) ?? 0) + 1);
      }
      if (t.reopenReason && t.updatedAt >= SCORECARD_WEEK.start) d.reopenedCount++;
      if (overdueSet.has(t.id)) overdueByUser.set(uid, (overdueByUser.get(uid) ?? 0) + 1);
      if (blockedSet.has(t.id)) blockedByUser.set(uid, (blockedByUser.get(uid) ?? 0) + 1);
    }

    return teamMembers.map((member) => {
      const uid = member.userId;
      const cap = capacityMap.get(uid) ?? { openTaskCount: 0, estimatedMinutes: 0, unestimatedTaskCount: 0 };
      const d = deriveMap.get(uid) ?? { completedThisWeek: 0, scoreSum: 0, scoreCount: 0, reopenedCount: 0 };
      return {
        userId: uid,
        displayName: member.fullName || member.email,
        completedThisWeek: d.completedThisWeek,
        openTaskCount: cap.openTaskCount,
        overdueCount: overdueByUser.get(uid) ?? 0,
        blockedCount: blockedByUser.get(uid) ?? 0,
        unestimatedOpenCount: cap.unestimatedTaskCount,
        estimatedMinutes: cap.estimatedMinutes,
        avgCompletionScore: d.scoreCount > 0 ? d.scoreSum / d.scoreCount : null,
        reopenedCount: d.reopenedCount,
        upcomingThisWeek: upcomingByUser.get(uid) ?? 0,
      };
    });
  }, [teamMembers, tasksResponse, mgmt]);

  const navigate = useNavigate();
  const handleMemberAskAI = async (s: ScorecardEntry) => {
    if (!workspaceId) return;
    trackAssistantLaunched('team_member', s.userId, 'capacity_review');
    try {
      const result = await createConversation({
        workspaceId,
        title: `Capacity review: ${s.displayName}`,
        mode: 'chat',
      }).unwrap();
      const sessionId = result.session.id;
      try {
        await addMessage({ sessionId, content: buildMemberAIPrimer(s) }).unwrap();
      } catch { /* context send failed — still navigate */ }
      navigate(`/conversations/${sessionId}?assistant=true`);
    } catch { /* silently ignore */ }
  };

  // Group members by clock-in status
  // THREE groups, not two.
  //
  // The server distinguishes "Central said this member is not clocked in"
  // from "Central did not answer" (`clockStateKnown`). Collapsing those into
  // one bucket renders the second as the first: a member would sit under
  // "Not Working" — a definite claim about where a person is — because a
  // service call failed. Unknown is its own group and says so.
  const { clockedIn, clockedOut, unknownState } = useMemo(() => {
    const clockedIn: TeamMemberSummary[] = [];
    const clockedOut: TeamMemberSummary[] = [];
    const unknownState: TeamMemberSummary[] = [];

    teamMembers?.forEach((member) => {
      // Absent means an older server that always answered — the pre-existing
      // behaviour, preserved.
      const known = member.clockStateKnown !== false;
      if (!known) {
        unknownState.push(member);
      } else if (member.isClockedIn) {
        clockedIn.push(member);
      } else {
        clockedOut.push(member);
      }
    });

    return { clockedIn, clockedOut, unknownState };
  }, [teamMembers]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ErrorState message="Failed to load team members." onRetry={refetch} />;
  }

  if (!activeWorkspace) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please select a workspace to view team.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Performance</h1>
          <p className="text-sm text-gray-500">
            {teamMembers?.length || 0} member{teamMembers?.length !== 1 ? 's' : ''} ·{' '}
            {clockedIn.length} clocked in
            {unknownState.length > 0 ? ` · ${unknownState.length} unavailable` : ''}
          </p>
        </div>
        {/* TEAM-UX-1 — this page is a READ/performance surface. Membership
            authoring (invite, role, removal) is Central-owned and lives in
            Settings → Members; duplicating it here created a second invite
            path into the same system. */}
        <Button
          variant="secondary"
          onClick={() => navigate('/settings?tab=members')}
          data-testid="team-manage-members-cta"
        >
          Manage workspace members
        </Button>
      </div>

      {/* Clocked In Section */}
      {clockedIn.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3 flex items-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            Currently Working ({clockedIn.length})
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {clockedIn.map((member) => (
              <TeamMemberCard
                key={member.userId}
                member={member}
                onClick={() => setSelectedMember(member)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Status unavailable — Central did not answer for these members. */}
      {unknownState.length > 0 && (
        <div data-testid="team-unknown-state">
          <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3 flex items-center gap-2">
            <span className="w-2 h-2 bg-amber-400 rounded-full" />
            Status Unavailable ({unknownState.length})
          </h2>
          <p className="text-sm text-gray-500 mb-3">
            We could not reach the attendance service, so we do not know whether these
            people are working right now.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {unknownState.map((member) => (
              <TeamMemberCard
                key={member.userId}
                member={member}
                onClick={() => setSelectedMember(member)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Team Members List */}
      <div>
        {/* Gated on clockedOut, not clockedIn. Keying this heading off a
            DIFFERENT group meant that whenever nobody was clocked in — every
            evening, every weekend — the clocked-out members still rendered
            but their heading and count vanished, leaving an unlabelled list
            of people in no stated state. The three-state contract only holds
            if each group labels itself. */}
        {clockedOut.length > 0 && (
          <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">
            Not Working ({clockedOut.length})
          </h2>
        )}

        {teamMembers?.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No Team Members</h3>
            <p className="text-gray-500 mb-4">
              Add people to this workspace to see their performance here.
            </p>
            <Button
              variant="secondary"
              onClick={() => navigate('/settings?tab=members')}
              data-testid="team-manage-members-cta-empty"
            >
              Manage workspace members
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Member
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden sm:table-cell">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(clockedIn.length > 0 ? clockedOut : teamMembers)?.map((member) => (
                    <tr
                      key={member.userId}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedMember(member)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            {member.avatarUrl ? (
                              <img
                                src={member.avatarUrl}
                                alt=""
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <span className="text-sm font-medium text-blue-600">
                                {(member.fullName || member.email).charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">
                              {member.fullName || member.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 hidden sm:table-cell">
                        {member.email}
                      </td>
                      <td className="px-4 py-3">
                        {(() => {
                          const badge = getBadgeClasses(member.role ?? '');
                          return (
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${badge.bg} ${badge.text}`}
                            >
                              {badge.label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        {member.clockStateKnown === false ? (
                          // Central did not answer. "Not working" would be a
                          // claim; this is the absence of one.
                          <span className="text-sm text-amber-700">Status unavailable</span>
                        ) : member.isClockedIn ? (
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-green-500 rounded-full" />
                            <span className="text-sm text-green-600">
                              Working · {member.clockInAt && formatElapsedTime(member.clockInAt)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">Not working</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Team Scorecard */}
      {scorecard.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
              Team Scorecard — This Week
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Completion, load, and risk signals per member
            </p>
          </div>
          <div className="divide-y divide-gray-100">
            {scorecard.map((s) => {
              const signals = deriveSignals(s);
              const estimatedH = s.estimatedMinutes > 0 ? Math.round(s.estimatedMinutes / 60) : null;
              return (
                <div key={s.userId} className="px-5 py-3.5 flex items-center gap-4 hover:bg-gray-50 transition-colors">
                  {/* Avatar */}
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-semibold text-blue-700 flex-shrink-0">
                    {s.displayName.charAt(0).toUpperCase()}
                  </div>

                  {/* Name + signals */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{s.displayName}</p>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {signals.map((sig) => (
                        <span
                          key={sig}
                          className={`text-xs px-1.5 py-0.5 rounded font-medium ${SIGNAL_CONFIG[sig]?.cls ?? 'bg-gray-100 text-gray-600'}`}
                        >
                          {sig}
                        </span>
                      ))}
                      {signals.length === 0 && s.openTaskCount === 0 && (
                        <span className="text-xs text-gray-400">No active tasks</span>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="hidden sm:flex items-center gap-4 flex-shrink-0">
                    <div className="text-center w-10">
                      <p className={`text-sm font-bold tabular-nums ${s.completedThisWeek > 0 ? 'text-green-600' : 'text-gray-300'}`}>
                        {s.completedThisWeek}
                      </p>
                      <p className="text-xs text-gray-400 leading-none mt-0.5">done</p>
                    </div>
                    <div className="text-center w-10">
                      <p className="text-sm font-bold text-gray-700 tabular-nums">{s.openTaskCount}</p>
                      <p className="text-xs text-gray-400 leading-none mt-0.5">open</p>
                    </div>
                    {s.overdueCount > 0 && (
                      <div className="text-center w-10">
                        <p className="text-sm font-bold text-red-600 tabular-nums">{s.overdueCount}</p>
                        <p className="text-xs text-gray-400 leading-none mt-0.5">overdue</p>
                      </div>
                    )}
                    {s.blockedCount > 0 && (
                      <div className="text-center w-10">
                        <p className="text-sm font-bold text-amber-600 tabular-nums">{s.blockedCount}</p>
                        <p className="text-xs text-gray-400 leading-none mt-0.5">blocked</p>
                      </div>
                    )}
                    {s.unestimatedOpenCount > 0 && (
                      <div className="text-center w-12">
                        <p className="text-sm font-bold text-gray-400 tabular-nums">{s.unestimatedOpenCount}</p>
                        <p className="text-xs text-gray-400 leading-none mt-0.5">no estimate</p>
                      </div>
                    )}
                    {estimatedH != null && (
                      <div className="text-center w-10">
                        <p className="text-sm font-bold text-gray-600 tabular-nums">~{estimatedH}h</p>
                        <p className="text-xs text-gray-400 leading-none mt-0.5">load</p>
                      </div>
                    )}
                    {s.avgCompletionScore != null && (
                      <div className="text-center w-12" title="Avg quality score">
                        <p className={`text-sm font-bold tabular-nums ${
                          s.avgCompletionScore >= 80 ? 'text-green-600'
                          : s.avgCompletionScore >= 60 ? 'text-yellow-600'
                          : 'text-red-500'
                        }`}>
                          {Math.round(s.avgCompletionScore)}
                        </p>
                        <p className="text-xs text-gray-400 leading-none mt-0.5">quality</p>
                      </div>
                    )}
                  </div>

                  {/* AI CTA */}
                  <button
                    onClick={() => handleMemberAskAI(s)}
                    className="flex-shrink-0 text-xs text-purple-600 hover:text-purple-800 font-medium hover:underline whitespace-nowrap"
                  >
                    Ask AI
                  </button>
                </div>
              );
            })}
          </div>
          <div className="px-5 py-2.5 border-t border-gray-100 bg-gray-50">
            <p className="text-xs text-gray-400">
              Overloaded: &gt;10 open tasks or &gt;8h load · At risk: overdue or blocked · Upcoming pressure: high load + 3+ tasks due this week · Needs estimates: &gt;3 unestimated · On top of work: completed this week, nothing overdue or blocked
            </p>
          </div>
        </div>
      )}

      {/* Rebalancing Recommendations */}
      {rebalancing && rebalancing.recommendations.length > 0 && (
        <div className="bg-white rounded-xl border border-amber-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-amber-100 bg-amber-50 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-amber-900">Rebalancing Recommendations</h2>
              <p className="text-xs text-amber-700 mt-0.5">
                {rebalancing.overloadedUsers.length} overloaded ·{' '}
                {rebalancing.underloadedUsers.length} underloaded ·{' '}
                {rebalancing.recommendations.length} suggested move{rebalancing.recommendations.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {rebalancing.recommendations.map((rec) => {
              const applied = appliedTaskIds.has(rec.taskId);
              return (
                <div
                  key={rec.taskId}
                  className={`px-5 py-4 flex items-start gap-4 ${applied ? 'opacity-50' : ''}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{rec.taskTitle}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1">
                        <span className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center text-xs font-medium text-red-700">
                          {rec.fromUserId.substring(0, 2).toUpperCase()}
                        </span>
                        <span className="text-gray-400">→</span>
                        <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center text-xs font-medium text-green-700">
                          {rec.toUserId.substring(0, 2).toUpperCase()}
                        </span>
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">{rec.reason}</p>
                  </div>
                  {/* Disabled rather than hidden: the RECOMMENDATION is
                      information a read-only principal may legitimately see
                      and act on by asking someone. Removing the row would
                      remove the insight; removing only the action is the
                      truthful thing. */}
                  <button
                    disabled={applied || !taskPerms.canWriteTasks}
                    title={
                      !taskPerms.canWriteTasks
                        ? 'You do not have permission to reassign tasks'
                        : undefined
                    }
                    onClick={() => handleApplyReassignment(rec.taskId, rec.toUserId)}
                    className="flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:cursor-default
                      bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-200 disabled:text-gray-500"
                  >
                    {applied ? 'Reassigned ✓' : 'Reassign →'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Team Member Detail Slide-Over */}
      <TeamMemberDetailSlideOver
        member={selectedMember}
        onClose={() => setSelectedMember(null)}
      />
    </div>
  );
}

// Team Member Card for Clocked In view
interface TeamMemberCardProps {
  member: TeamMemberSummary;
  onClick: () => void;
}

function TeamMemberCard({ member, onClick }: TeamMemberCardProps) {
  return (
    <div
      className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
          {member.avatarUrl ? (
            <img
              src={member.avatarUrl}
              alt=""
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <span className="text-lg font-medium text-blue-600">
              {(member.fullName || member.email).charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-gray-900 truncate">
            {member.fullName || member.email}
          </p>
          <span
            className={`text-xs ${getBadgeClasses(member.role ?? '').text}`}
          >
            {getBadgeClasses(member.role ?? '').label}
          </span>
        </div>
      </div>

      {/* The status is READ from the member, not assumed.
          This card used to be rendered only for the clocked-in list, so it
          hardcoded "Working" with a live green dot. It is now also used for
          members whose state Central could not report, and telling those
          people's colleagues they are working would be worse than the
          "not working" this change set out to remove. */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        {member.clockStateKnown === false ? (
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-amber-400 rounded-full" />
            <span className="text-sm font-medium text-amber-700">Status unavailable</span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-sm font-medium text-green-600">Working</span>
          </div>
        )}
        {member.clockStateKnown !== false && member.clockInAt && (
          <span className="text-sm text-gray-500">
            {formatElapsedTime(member.clockInAt)}
          </span>
        )}
      </div>
    </div>
  );
}
